var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { base64Decoder, booleanDecoder, customElement, PICBase, stringDecoder, stringDecoderLC } from '../Internals';
import { setupABL } from './util/languageABL';
import { property } from 'lit/decorators.js';
let PICEditor = class PICEditor extends PICBase {
    constructor() {
        super(...arguments);
        this.container = createRef();
        this.editorData_ = "";
        this.editorLanguage = 'json';
        this.editorHeight_ = '90vh';
        this.editorWidth_ = '90vw';
        this.enableMinimap = true;
        this.autoFormat = true;
    }
    set editorHeight(iStr) {
        this.editorHeight_ = iStr;
        this.requestUpdate();
    }
    set editorWidth(iStr) {
        this.editorWidth_ = iStr;
        this.requestUpdate();
    }
    get editorHeight() {
        return this.editorHeight_;
    }
    get editorWidth() {
        return this.editorWidth_;
    }
    set editorData(iData) {
        this.editorData_ = iData;
        if (this.editor) {
            this.editor.setValue(this.editorData_);
        }
        this.requestUpdate();
    }
    checkAndConsumeInlineData() {
        let iData = this.getAttribute('datafromprogress');
        if (iData) {
            this.editorData_ = base64Decoder(iData);
            if (this.editor) {
                this.editor.setValue(this.editorData_);
            }
            this.requestUpdate();
        }
        this.removeAttribute('datafromprogress');
    }
    get editorData() {
        if (this.editor)
            return this.editor.getValue();
        else
            return this.editorData_;
    }
    static get styles() {
        const defaultStyle = css `
    .container {
        border: 1px solid #ccc;
      }
    `;
        return css `
            ${super.styles}
            ${defaultStyle}
        `;
    }
    //Keep this, else focus call from monaco will create picbase in yet implemented error
    focus() {
    }
    isMonacoReady() {
        return (window.monaco && window.monaco.editor);
    }
    connectedCallback() {
        super.connectedCallback();
        this.checkAndConsumeInlineData();
    }
    firstUpdated() {
        this.setupMonaco();
    }
    setupXML() {
        window.monaco.languages.html.registerHTMLLanguageService("xml", {
            format: {
                wrapLineLength: 0,
                preserveNewLines: false,
            }
        }, { documentFormattingEdits: true });
    }
    setupMonaco() {
        if (!this.editor) {
            if (this.isMonacoReady()) {
                //TODO: continue the work on ABL custom language
                if (this.editorLanguage === 'abl')
                    setupABL();
                else if (this.editorLanguage === 'xml')
                    this.setupXML();
                this.setEditor();
            }
            else {
                console.log('setupMonaco: Monaco is not loaded into global API');
            }
        }
    }
    setEditor() {
        let settings = {
            value: this.editorData,
            language: this.editorLanguage,
            theme: this.editorLanguage === 'abl' ? 'ablTheme' : undefined,
            // automaticLayout: true, //TODO: make own version of this, with big files it will take forever to load new page
            scrollBeyondLastLine: false,
            readOnly: this.disabled_,
            foldingMaximumRegions: 50000
        };
        if (!this.enableMinimap)
            settings = Object.assign(Object.assign({}, settings), {
                minimap: { enabled: this.enableMinimap },
                overviewRulerLanes: 0,
                hideCursorInOverviewRuler: true,
                scrollbar: {
                    vertical: 'hidden',
                    horizontalScrollbarSize: '4px',
                },
                overviewRulerBorder: false,
                readOnly: this.disabled_
            });
        this.editor = window.monaco.editor.create(this.container.value, settings);
        this.doAutoFormat();
    }
    wait(iTime) {
        return new Promise(r => setTimeout(() => { return r('waited'); }, iTime));
    }
    updateReadOnly(isReadOnly) {
        this.editor.updateOptions({ readOnly: isReadOnly });
    }
    formatDocument() {
        return new Promise(r => {
            try {
                this.editor.getAction('editor.action.formatDocument').run();
            }
            catch (_a) { }
            return r('formatting done');
        });
    }
    doAutoFormat() {
        if (this.autoFormat)
            if (this.disabled_)
                this.wait(1)
                    .then(() => this.updateReadOnly(false))
                    .then(() => this.wait(200))
                    .then(() => this.formatDocument())
                    .then(() => this.wait(100))
                    .then(() => this.updateReadOnly(true));
            else
                this.wait(200).then(() => this.formatDocument());
    }
    getContainerStyle() {
        return {
            height: `${this.editorHeight}rem;`
        };
    }
    render() {
        return html `
      ${this.renderCSS(true)}
      <link rel="stylesheet" href="/scripts/picit/monaco/editor.main.css">
      <style>
        ${`
          .container {
            border: 1px solid #ccc;
            height: ${this.editorHeight};
            width: ${this.editorWidth};

          }
          `}
    </style>
    <div class="container" ${ref(this.container)}>
    </div>
    `;
    }
};
__decorate([
    property({ converter: stringDecoderLC, attribute: 'editorlanguage' })
], PICEditor.prototype, "editorLanguage", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: 'enableminimap' })
], PICEditor.prototype, "enableMinimap", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: 'autoformat' })
], PICEditor.prototype, "autoFormat", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: 'editorheight' })
], PICEditor.prototype, "editorHeight", null);
__decorate([
    property({ converter: stringDecoder, attribute: 'editorwidth' })
], PICEditor.prototype, "editorWidth", null);
__decorate([
    property({ converter: base64Decoder, attribute: 'editordata' })
], PICEditor.prototype, "editorData", null);
PICEditor = __decorate([
    customElement('pic-editor')
], PICEditor);
export { PICEditor };
//# sourceMappingURL=PICEditor.js.map